import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Autocomplete, Box, FormControl, IconButton, InputAdornment, MenuItem, Select, Skeleton, Table,
  TableBody, TableCell, TableHead, TableRow, TableSortLabel, TextField, Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ClockOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { DateTime } from "luxon";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import "./TheRundown.css";
import { exportProcessActivityReport, getProcessActivityReport } from "../services/processReportApi";
import { getProjectSupervisorOptions } from "../services/attendanceApi";

const PAGE_SIZES = [10, 25, 50, 100];
const STATUS_OPTIONS = [
  ["all", "All Processes"],
  ["not_started", "Not Started"],
  ["activity_recorded", "In Progress + Completed"],
  ["in_progress", "In Progress"],
  ["completed", "Completed"],
];

const selectedProjectId = () => {
  try {
    const stored = JSON.parse(localStorage.getItem("selectedProject") || "null");
    return String(stored?.id || stored || "").trim();
  } catch { return ""; }
};

const formatDateTime = (value, timeZone, withTime = true) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  try {
    return new Intl.DateTimeFormat("en-IN", {
      timeZone: timeZone || undefined,
      day: "2-digit", month: "short", year: "numeric",
      ...(withTime ? { hour: "2-digit", minute: "2-digit", hour12: true } : {}),
    }).format(date);
  } catch { return "—"; }
};

const formatDateTimeParts = (value, timeZone) => {
  if (!value) return { date: "—", time: "" };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: "—", time: "" };
  try {
    return {
      date: new Intl.DateTimeFormat("en-IN", { timeZone: timeZone || undefined, day: "2-digit", month: "short", year: "numeric" }).format(date),
      time: new Intl.DateTimeFormat("en-IN", { timeZone: timeZone || undefined, hour: "2-digit", minute: "2-digit", hour12: true }).format(date),
    };
  } catch {
    return { date: date.toLocaleDateString("en-IN"), time: date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }) };
  }
};

const pickerDate = (value, timeZone) => {
  if (!value) return null;
  const date = DateTime.fromISO(value, { zone: timeZone || undefined });
  return date.isValid ? date : null;
};

const FILTER_MENU_PROPS = {
  slotProps: {
    paper: { className: "rundown-menu-paper" },
    list: { className: "rundown-menu-list" },
  },
};

function TheRundown({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const [projectId, setProjectId] = useState(selectedProjectId);
  const [filters, setFilters] = useState({ status: "all", dateBasis: "planned", dateFrom: "", dateTo: "", performedBy: "", search: "" });
  const [searchInput, setSearchInput] = useState("");
  const [performers, setPerformers] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sort, setSort] = useState({ field: "planned_start", direction: "desc" });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [projectTimezone, setProjectTimezone] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [exporting, setExporting] = useState(false);
  const requestId = useRef(0);

  useEffect(() => {
    const onProjectChange = () => { setProjectId(selectedProjectId()); setPage(1); };
    window.addEventListener("projectChanged", onProjectChange);
    return () => window.removeEventListener("projectChanged", onProjectChange);
  }, []);

  useEffect(() => {
    if (!projectId) { setPerformers([]); return undefined; }
    const controller = new AbortController();
    getProjectSupervisorOptions(projectId, controller.signal)
      .then((users) => setPerformers(
        users
          .map((user) => ({ user_id: user?.user_id, name: user?.user_name || user?.name || "" }))
          .filter((user) => user.user_id && user.name),
      ))
      .catch((requestError) => { if (requestError?.code !== "ERR_CANCELED") setPerformers([]); });
    return () => controller.abort();
  }, [projectId]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const search = searchInput.trim();
      setFilters((current) => current.search === search ? current : { ...current, search });
      setPage(1);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const query = useMemo(() => ({ ...filters, page, pageSize, sortBy: sort.field, sortDirection: sort.direction }), [filters, page, pageSize, sort]);
  const loadReport = useCallback(() => {
    if (!projectId) { setRows([]); setTotal(0); setLoading(false); return () => {}; }
    const controller = new AbortController();
    const currentRequest = ++requestId.current;
    setLoading(true); setError("");
    getProcessActivityReport(projectId, query, controller.signal)
      .then((result) => {
        if (currentRequest !== requestId.current) return;
        setRows(Array.isArray(result?.data) ? result.data : []);
        setTotal(Number(result?.total) || 0);
        setProjectTimezone(result?.project_tz || "");
      })
      .catch((requestError) => {
        if (requestError?.code === "ERR_CANCELED" || currentRequest !== requestId.current) return;
        setRows([]); setTotal(0);
        setError(requestError?.response?.data?.message || "Unable to load The Rundown.");
      })
      .finally(() => { if (currentRequest === requestId.current) setLoading(false); });
    return () => controller.abort();
  }, [projectId, query]);
  useEffect(() => loadReport(), [loadReport, refreshKey]);

  const updateFilter = (key, value) => { setFilters((current) => ({ ...current, [key]: value })); setPage(1); };
  const clearFilters = () => { setFilters({ status: "all", dateBasis: "planned", dateFrom: "", dateTo: "", performedBy: "", search: "" }); setSearchInput(""); setPage(1); };
  const hasActiveFilters = Boolean(
    filters.status !== "all" || filters.dateBasis !== "planned" || filters.dateFrom ||
    filters.dateTo || filters.performedBy || filters.search || searchInput,
  );
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const updateSort = (field) => {
    setSort((current) => ({
      field,
      direction: current.field === field && current.direction === "asc" ? "desc" : "asc",
    }));
    setPage(1);
  };
  const sortableHeader = (field, label) => <Tooltip title={`Sort all report records by ${label}`}><TableSortLabel hideSortIcon={false} active={sort.field === field} direction={sort.field === field ? sort.direction : "asc"} aria-label={`Sort all report records by ${label}`} onClick={() => updateSort(field)}>{label}</TableSortLabel></Tooltip>;
  const activityDateTime = (value) => {
    const { date, time } = formatDateTimeParts(value, projectTimezone);
    return <span className="rundown-activity-time"><span>{date}</span>{time && <span>{time}</span>}</span>;
  };
  const exportReport = async () => {
    if (!projectId || exporting) return;
    setExporting(true);
    try {
      const response = await exportProcessActivityReport(projectId, query);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `the_rundown_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (exportError) {
      console.error("Unable to export The Rundown:", exportError);
      setError("Unable to export The Rundown. Please try again.");
    } finally {
      setExporting(false);
    }
  };
  const schedule = (value) => <span className={`rundown-schedule ${String(value || "no-plan").toLowerCase().replace(/\s+/g, "-")}`}>{value || "No plan"}</span>;
  const openProcess = (row) => row?.equipment_id && navigate(`/equipment-details/${row.equipment_id}?process_id=${encodeURIComponent(row.process_id)}`);
  const status = (value) => value === "Completed"
    ? <span className="rundown-status completed"><CheckCircleOutlineIcon />Completed</span>
    : value === "In Progress"
      ? <span className="rundown-status in-progress"><ClockOutlinedIcon />In Progress</span>
      : <span className="rundown-status muted">{value || "Not Started"}</span>;

  return <div className={`table-container rundown-page ${isSidebarExpanded ? "expanded" : "collapsed"}`}>
    <div className="rundown-content">
      <header className="rundown-header">
        <div>
          <h2>The Rundown</h2>
          <p>Project process activity and progress.{projectTimezone && <> <span aria-hidden="true">·</span> Project timezone: {projectTimezone}</>}</p>
        </div>
        <div className="rundown-header-actions">
          <button type="button" className="rundown-clear" onClick={clearFilters} disabled={!hasActiveFilters}>
            <CloseIcon fontSize="small" aria-hidden="true" /> Clear filters
          </button>
          <Tooltip title="Export all filtered records to Excel">
            <span>
              <IconButton className="rundown-export" aria-label="Export filtered report to Excel" disabled={loading || exporting || !projectId} onClick={exportReport}>
                <FileDownloadOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Refresh">
            <span>
              <IconButton className="rundown-refresh" aria-label="Refresh report" disabled={loading} onClick={() => setRefreshKey((value) => value + 1)}>
                <RefreshIcon className={loading ? "rundown-spin" : ""} />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </header>
      <section className="rundown-filters" aria-label="The Rundown filters">
        <FormControl className="rundown-filter rundown-status-filter" size="small"><Select value={filters.status} onChange={(event) => updateFilter("status", event.target.value)} inputProps={{ "aria-label": "Process status" }} MenuProps={FILTER_MENU_PROPS}>{STATUS_OPTIONS.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</Select></FormControl>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <div className="rundown-date-group" role="group" aria-label="Date filter">
            <FormControl className="rundown-date-basis" size="small"><Select value={filters.dateBasis} onChange={(event) => updateFilter("dateBasis", event.target.value)} inputProps={{ "aria-label": "Date basis" }} MenuProps={FILTER_MENU_PROPS}><MenuItem value="planned">Planned date</MenuItem><MenuItem value="actual">Actual date</MenuItem></Select></FormControl>
            <DatePicker value={pickerDate(filters.dateFrom, projectTimezone)} onChange={(value) => updateFilter("dateFrom", value?.toISODate() || "")} timezone={projectTimezone || undefined} format="dd-MM-yyyy" slotProps={{ textField: { size: "small", className: "rundown-date-picker", inputProps: { "aria-label": "From date", placeholder: "From date" } }, popper: { className: "rundown-date-popper" } }} />
            <span className="rundown-date-to" aria-hidden="true">to</span>
            <DatePicker value={pickerDate(filters.dateTo, projectTimezone)} onChange={(value) => updateFilter("dateTo", value?.toISODate() || "")} minDate={pickerDate(filters.dateFrom, projectTimezone) || undefined} timezone={projectTimezone || undefined} format="dd-MM-yyyy" slotProps={{ textField: { size: "small", className: "rundown-date-picker", inputProps: { "aria-label": "To date", placeholder: "To date" } }, popper: { className: "rundown-date-popper" } }} />
          </div>
        </LocalizationProvider>
        <div className="rundown-user-search-pair">
          <Autocomplete className="rundown-performer" size="small" options={performers} value={performers.find((person) => person.user_id === filters.performedBy) || null} onChange={(_, person) => updateFilter("performedBy", person?.user_id || "")} getOptionLabel={(person) => person?.name || ""} isOptionEqualToValue={(option, value) => option.user_id === value.user_id} noOptionsText="No project users found" slotProps={{ paper: { className: "rundown-performer-popper-paper" }, listbox: { className: "rundown-performer-options" } }} renderInput={(params) => <TextField {...params} placeholder="All project users" inputProps={{ ...params.inputProps, "aria-label": "Filter by performed by" }} />} />
          <TextField className="rundown-search" size="small" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search equipment or process" inputProps={{ "aria-label": "Search equipment or process" }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>, endAdornment: searchInput ? <InputAdornment position="end"><IconButton size="small" aria-label="Clear search" onClick={() => setSearchInput("")}><CloseIcon fontSize="small" /></IconButton></InputAdornment> : null }} />
        </div>
      </section>
      <section className="rundown-table-card" aria-busy={loading}>
        
        {error ? <div className="rundown-message"><p>{error}</p><button type="button" onClick={() => setRefreshKey((value) => value + 1)}>Try again</button></div> : <div className="rundown-scroll"><Table size="small" className="rundown-table" sx={{ minWidth: 1400 }}><TableHead><TableRow><TableCell align="center" className="rundown-serial">Sr. No.</TableCell><TableCell>Equipment</TableCell><TableCell>Process</TableCell><TableCell>{sortableHeader("started_by", "Started by")}</TableCell><TableCell>{sortableHeader("ended_by", "Ended by")}</TableCell><TableCell>{sortableHeader("planned_start", "Planned start")}</TableCell><TableCell>{sortableHeader("planned_end", "Planned end")}</TableCell><TableCell>Status</TableCell><TableCell>Plan vs delay</TableCell></TableRow></TableHead><TableBody>{loading && Array.from({ length: 8 }, (_, index) => <TableRow key={index}>{Array.from({ length: 9 }, (__, cell) => <TableCell key={cell}><Skeleton variant="rounded" height={22} /></TableCell>)}</TableRow>)}{!loading && rows.map((row, index) => <TableRow key={row.process_id} className={`rundown-row ${row.equipment_id ? "is-clickable" : ""}`} hover role={row.equipment_id ? "button" : undefined} tabIndex={row.equipment_id ? 0 : undefined} onClick={() => openProcess(row)} onKeyDown={(event) => { if ((event.key === "Enter" || event.key === " ") && row.equipment_id) { event.preventDefault(); openProcess(row); } }}><TableCell align="center" className="rundown-serial">{(page - 1) * pageSize + index + 1}</TableCell><TableCell className={row.equipment_number ? "rundown-equipment" : "rundown-milestone"}>{row.equipment_number ? <><span>{row.equipment_number}</span><Tooltip title="Open equipment process"><IconButton className="rundown-go-to" size="small" aria-label={`Open ${row.equipment_number}`} onClick={(event) => { event.stopPropagation(); openProcess(row); }} onKeyDown={(event) => event.stopPropagation()}><OpenInNewIcon /></IconButton></Tooltip></> : (row.is_milestone ? "Project milestone" : "—")}</TableCell><TableCell className="rundown-process"><strong title={row.process_name || "—"}>{row.process_name || "—"}</strong>{row.process_code && <span className="rundown-code">· {row.process_code}</span>}</TableCell><TableCell><span className="rundown-person-activity"><span>{row.started_by || "—"}</span>{activityDateTime(row.actual_start)}</span></TableCell><TableCell><span className="rundown-person-activity"><span>{row.ended_by || "—"}</span>{activityDateTime(row.actual_end)}</span></TableCell><TableCell>{activityDateTime(row.start_eta)}</TableCell><TableCell>{activityDateTime(row.finish_eta)}</TableCell><TableCell>{status(row.status)}</TableCell><TableCell>{schedule(row.schedule_indicator)}</TableCell></TableRow>)}{!loading && rows.length === 0 && <TableRow><TableCell colSpan={9} align="center" sx={{ py: 7, color: "#667085" }}>No processes match the selected filters.</TableCell></TableRow>}</TableBody></Table></div>}
        {!error && <footer className="rundown-pagination"><div className="rundown-record-count">Total Records: <strong>{total.toLocaleString("en-IN")}</strong></div><div className="rundown-pagination-controls"><span>Per Page:</span><FormControl size="small"><Select value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }}>{PAGE_SIZES.map((size) => <MenuItem key={size} value={size}>{size}</MenuItem>)}</Select></FormControl><IconButton className="rundown-page-button" aria-label="Previous page" disabled={loading || page <= 1} onClick={() => setPage((value) => value - 1)}><NavigateBeforeIcon /></IconButton><span>Page <strong>{page}</strong> of <strong>{totalPages}</strong></span><IconButton className="rundown-page-button" aria-label="Next page" disabled={loading || page >= totalPages} onClick={() => setPage((value) => value + 1)}><NavigateNextIcon /></IconButton></div></footer>}
         
      </section>
    </div>
  </div>;
}
export default TheRundown;
